/**
 * Modal lightbox para visualização de mídias (imagens e vídeos) em tela cheia
 *
 * Suporta: zoom (botões, slider e teclado), rotação 90°, download,
 * navegação por teclado (Esc para fechar), navegação de galeria com
 * setas (esquerda/direita), player de vídeo integrado e documento via iframe
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-03 v2.0.0 - Refatoração visual conforme modelo de referência:
 *            fundo escuro fixo, barra de ferramentas em pílula (zoom +/−, girar
 *            90° →/←, baixar) no canto superior esquerdo, botão fechar isolado no
 *            canto superior direito, legenda na base e slider de zoom com
 *            percentual (25%–400%). Zoom/rotação aplicados via transform e
 *            resetados ao trocar de mídia. Atalhos: +/− zoom, R/Shift+R rotação.
 *   RELEASE: versões anteriores - Galeria com setas/indicadores, download,
 *            abrir em nova aba, vídeo e documento via iframe.
 */

import { useEffect, useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  X,
  Download,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  RotateCcw,
} from 'lucide-react';
import { getStorageUrl } from '@/lib/storage';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';

/** Item de mídia para o lightbox */
export interface MediaItem {
  /** URL ou caminho relativo da mídia */
  url: string;
  /** Tipo da mídia: image, video ou document (PDF/Word via iframe) */
  type: 'image' | 'video' | 'document';
  /** Título opcional para exibição no header */
  title?: string;
}

interface ImageLightboxProps {
  /** Se o lightbox está aberto */
  open: boolean;
  /** Callback para fechar o lightbox */
  onClose: () => void;
  /** URL ou caminho relativo da imagem (modo single - legado) */
  imageUrl?: string;
  /** Legenda da mídia (opcional) */
  caption?: string | null;
  /** Array de URLs para modo galeria (legado - apenas imagens) */
  images?: string[];
  /** Array de mídias para modo galeria (novo - imagens e vídeos) */
  mediaItems?: MediaItem[];
  /** Índice inicial para modo galeria */
  initialIndex?: number;
}

/** Limites e passo do zoom (em %) */
const ZOOM_MIN = 25;
const ZOOM_MAX = 400;
const ZOOM_STEP = 25;

export function ImageLightbox({
  open,
  onClose,
  imageUrl,
  caption,
  images,
  mediaItems,
  initialIndex = 0,
}: ImageLightboxProps) {
  // Estado para índice atual da galeria
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  // Estado de zoom (em %) e rotação (graus, passos de 90) — apenas imagens
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);

  // Converte para lista de MediaItems unificada
  const mediaList: MediaItem[] = mediaItems && mediaItems.length > 0
    ? mediaItems
    : images && images.length > 0
      ? images.map(url => ({ url, type: 'image' as const }))
      : imageUrl
        ? [{ url: imageUrl, type: 'image' as const }]
        : [];

  // Determina se está em modo galeria
  const isGalleryMode = mediaList.length > 1;

  // Item atual baseado no índice
  const currentItem = mediaList[currentIndex] || { url: '', type: 'image' };
  const fullUrl = getStorageUrl(currentItem.url) || currentItem.url;
  const isVideo = currentItem.type === 'video';
  const isDocument = currentItem.type === 'document';
  const isImage = currentItem.type === 'image';

  // Reseta índice quando dialog abre com novo initialIndex
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  // Reseta zoom e rotação ao trocar de mídia ou ao abrir
  useEffect(() => {
    setZoom(100);
    setRotation(0);
  }, [open, currentIndex]);

  // Navegação para mídia anterior
  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : mediaList.length - 1));
  }, [mediaList.length]);

  // Navegação para próxima mídia
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev < mediaList.length - 1 ? prev + 1 : 0));
  }, [mediaList.length]);

  // Handlers de zoom respeitando os limites
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(ZOOM_MAX, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(ZOOM_MIN, prev - ZOOM_STEP));
  }, []);

  // Handlers de rotação em passos de 90° (acumulativo)
  const handleRotateCw = useCallback(() => {
    setRotation(prev => (prev + 90) % 360);
  }, []);

  const handleRotateCcw = useCallback(() => {
    setRotation(prev => (prev - 90 + 360) % 360);
  }, []);

  // Keyboard handler para navegação, zoom, rotação e fechar
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && isGalleryMode) {
      goToPrevious();
    } else if (e.key === 'ArrowRight' && isGalleryMode) {
      goToNext();
    } else if (isImage && (e.key === '+' || e.key === '=')) {
      handleZoomIn();
    } else if (isImage && e.key === '-') {
      handleZoomOut();
    } else if (isImage && (e.key === 'r' || e.key === 'R')) {
      // Shift+R gira para a esquerda; R gira para a direita
      if (e.shiftKey) {
        handleRotateCcw();
      } else {
        handleRotateCw();
      }
    }
  }, [onClose, isGalleryMode, isImage, goToPrevious, goToNext, handleZoomIn, handleZoomOut, handleRotateCw, handleRotateCcw]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  // Handler para download da mídia
  const handleDownload = async () => {
    try {
      const response = await fetch(fullUrl);
      const blob = await response.blob();

      // Extrai nome do arquivo da URL
      const urlParts = fullUrl.split('/');
      const filename = urlParts[urlParts.length - 1] || (isVideo ? 'video.mp4' : isDocument ? 'documento.pdf' : 'imagem.jpg');

      // Cria link temporário para download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[ImageLightbox] Error downloading media:', error);
      // Fallback: abre em nova aba
      window.open(fullUrl, '_blank');
    }
  };

  // Handler para abrir em nova aba
  const handleOpenInNewTab = () => {
    window.open(fullUrl, '_blank');
  };

  // Estilo padrão dos botões circulares da barra de ferramentas (fundo escuro)
  const toolbarButtonClass =
    'h-9 w-9 rounded-full text-white/80 hover:text-white hover:bg-white/10';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 backdrop-blur-sm border-none"
        preventCloseOnOutsideInteraction={false}
        /* Oculta o X padrão do Dialog: o lightbox já possui seu próprio botão de fechar no canto superior direito */
        hideCloseButton
      >
        {/* Título acessível (visualmente oculto) */}
        <VisuallyHidden>
          <DialogTitle>
            {isDocument ? 'Visualização de Documento' : isVideo ? 'Visualização de Vídeo' : 'Visualização de Imagem'}
          </DialogTitle>
        </VisuallyHidden>

        {/* Barra de ferramentas em pílula (canto superior esquerdo) */}
        <div className="absolute top-4 left-4 z-10 flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 backdrop-blur-sm">
          {isImage && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomIn}
                disabled={zoom >= ZOOM_MAX}
                className={toolbarButtonClass}
                title="Ampliar (+)"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleZoomOut}
                disabled={zoom <= ZOOM_MIN}
                className={toolbarButtonClass}
                title="Reduzir (-)"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRotateCw}
                className={toolbarButtonClass}
                title="Girar 90° para a direita (R)"
              >
                <RotateCw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRotateCcw}
                className={toolbarButtonClass}
                title="Girar 90° para a esquerda (Shift+R)"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDownload}
            className={toolbarButtonClass}
            title="Baixar mídia"
          >
            <Download className="h-4 w-4" />
          </Button>
          {(isVideo || isDocument) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleOpenInNewTab}
              className={toolbarButtonClass}
              title="Abrir em nova aba"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Contador da galeria (topo central) */}
        {isGalleryMode && mediaList.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 rounded-full bg-black/60 px-3 py-1.5 backdrop-blur-sm">
            <p className="text-sm font-medium text-white/90">
              {currentIndex + 1} de {mediaList.length}
            </p>
          </div>
        )}

        {/* Botão fechar isolado (canto superior direito) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 h-10 w-10 rounded-full border border-white/40 text-white/90 hover:text-white hover:bg-white/10"
          title="Fechar (Esc)"
        >
          <X className="h-5 w-5" />
        </Button>

        {/* Botões de navegação (modo galeria) */}
        {isGalleryMode && mediaList.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              className={cn(
                'absolute left-2 top-1/2 -translate-y-1/2 z-10',
                'h-10 w-10 rounded-full bg-black/60 hover:bg-black/80',
                'text-white/70 hover:text-white'
              )}
              title="Mídia anterior (←)"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2 z-10',
                'h-10 w-10 rounded-full bg-black/60 hover:bg-black/80',
                'text-white/70 hover:text-white'
              )}
              title="Próxima mídia (→)"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}

        {/* Container da mídia */}
        <div
          className={cn(
            "flex items-center justify-center p-8 cursor-pointer",
            isDocument ? "min-h-[70vh]" : "min-h-[50vh]"
          )}
          onClick={onClose}
        >
          {isDocument ? (
            // Documento via iframe (PDF renderiza nativamente no browser)
            <iframe
              key={fullUrl}
              src={fullUrl}
              title={currentItem.title || 'Documento'}
              className="w-full h-[80vh] rounded-lg shadow-lg bg-white"
              onClick={(e) => e.stopPropagation()}
            />
          ) : isVideo ? (
            // Player de vídeo
            <video
              key={fullUrl}
              src={fullUrl}
              controls
              autoPlay
              className="max-w-full max-h-[85vh] rounded-lg shadow-lg"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            // Imagem com zoom e rotação; scroll interno quando ampliada
            <div
              className="max-w-full max-h-[85vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={fullUrl}
                alt={caption || `Imagem ${currentIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-lg transition-transform duration-200"
                style={{ transform: `scale(${zoom / 100}) rotate(${rotation}deg)` }}
              />
            </div>
          )}
        </div>

        {/* Rodapé: legenda centralizada e slider de zoom (apenas imagens) */}
        <div className="absolute bottom-4 left-0 right-0 z-10 flex flex-col items-center gap-3 px-4 pointer-events-none">
          {caption && (
            <p className="text-sm text-white/90 text-center max-w-2xl truncate">
              {caption}
            </p>
          )}
          {isImage && (
            <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-black/60 px-4 py-2 backdrop-blur-sm w-64">
              <ZoomOut className="h-4 w-4 text-white/70 shrink-0" />
              <Slider
                value={[zoom]}
                min={ZOOM_MIN}
                max={ZOOM_MAX}
                step={ZOOM_STEP}
                onValueChange={([value]) => setZoom(value)}
                aria-label="Controle de zoom"
                className="flex-1"
              />
              <ZoomIn className="h-4 w-4 text-white/70 shrink-0" />
              <span className="text-xs font-medium text-white/90 tabular-nums w-10 text-right">
                {zoom}%
              </span>
            </div>
          )}
        </div>

        {/* Indicadores de posição (modo galeria) */}
        {isGalleryMode && mediaList.length > 1 && mediaList.length <= 10 && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2">
            {mediaList.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  index === currentIndex
                    ? 'bg-white'
                    : 'bg-white/30 hover:bg-white/50'
                )}
                aria-label={`Ir para mídia ${index + 1}`}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
